import { useContext, useEffect, useMemo, useState } from 'react';

import { AppContext } from '@r-app/app-context';
import { CreateCornerPattern } from '../symbols';
import { DIMENSION_FONT_SIZE } from '@r-core/constants';
import { DisplayMode } from '@r-core';
import { getFlatTrimGroups, getFlatTrimLayout } from './helpers';
import { RenderFlatTrimGroup } from './render-flat-trim-group';
import { renderTracking } from '@r-services/render-tracking';

export const RenderCornerTrims = (): React.ReactElement => {
	const { controller: { options, kitchen, navigation, viewport }} = useContext(AppContext);

	const [trimVersion, setTrimVersion] = useState(0);

	useEffect(() => {
		const subscriptionIds = [
			kitchen.subscribe('panel_trim', '*', () => setTrimVersion((v) => v + 1)),
			kitchen.subscribe('panel_trims', '*', () => setTrimVersion((v) => v + 1)),
			kitchen.subscribe('island_size', '*', () => setTrimVersion((v) => v + 1))
		];

		return () => {
			subscriptionIds.forEach((subscriptionId) => kitchen.unsubscribe(subscriptionId));
		};
	}, [kitchen]);

	// fitToView() is not gated by viewport.config.autoZoom (unlike triggerAutoZoom()), which this app leaves off; poll via rAF until the svg is registered, then fit once
	useEffect(() => {
		let cancelled = false;
		let rafId: number | null = null;

		const attemptFit = () => {
			if (cancelled) return;

			const { width, height } = viewport.containerSize;
			const isReady = width > 0 && height > 0 && viewport.viewerSize !== null && viewport.getSvgElement() !== null;

			if (isReady) {
				viewport.actions.fitToView();
				return;
			}

			rafId = requestAnimationFrame(attemptFit);
		};

		attemptFit();

		return () => {
			cancelled = true;
			if (rafId !== null) cancelAnimationFrame(rafId);
		};
	}, [viewport]);

	const trimGroups = useMemo(() => getFlatTrimGroups(kitchen), [kitchen, trimVersion]);

	renderTracking.incrementRender('RenderCornerTrims');

	if (trimGroups.length === 0) return <></>;

	const isManufacturingMode = navigation.displayMode === DisplayMode.Manufacturing ||
		navigation.displayMode === DisplayMode.CornerTrimManufacturing;
	const showTexture = !isManufacturingMode;
	const includeLabelBox = !isManufacturingMode;

	const fontSize = 2 * options.dimensionFontSizeFactor;
	const dimensionFontSize = DIMENSION_FONT_SIZE * options.dimensionFontSizeFactor;

	const { pieces, baselineY, nameSuffixY } = getFlatTrimLayout({
		groups: trimGroups,
		dimensionFontSize,
		cornerScale: kitchen.material?.surround?.cornerScale ?? 1
	});

	return (
		<g id="corner-trims-view">
			{showTexture && <CreateCornerPattern {...{ material: kitchen.material, options }} />}

			{pieces.map((piece, groupIndex) => (
				<RenderFlatTrimGroup
					key={`corner-trim-group-${groupIndex}`}
					{...{ piece, groupIndex, baselineY, nameSuffixY, showTexture, includeLabelBox, fontSize, dimensionFontSize }}
				/>
			))}
		</g>
	);
};
